import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../../../ui/YhInput";
import YhButton from "../../../../../../../../ui/YhButton";
import {
  addDepartment,
  deleteDepartment,
  getAllDepartment,
} from "./CreateService";
import customErrorHandler from "../../../../../../../../utils/customErrorHandler";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../../../helpers/methods";
import notifiers from "../../../../../../../../global/constants/NotificationConstants";
import { editDepartment } from "../../../AddTeamMetaList/CreateService";

interface CustomProps {
  backStep: Function;
  handleCloseCreateTeam: Function;
  nextStep?: () => void | undefined;
  selectedDept: any;
  setSelectedDept: Function;
  hideFooterButtons?: boolean;
}

const DEPT_ICONS = [
  { id: "building", label: "Building" },
  { id: "users", label: "People" },
  { id: "monitor", label: "Computer" },
  { id: "code", label: "Code" },
  { id: "chart", label: "Chart" },
  { id: "briefcase", label: "Briefcase" },
  { id: "settings", label: "Settings" },
] as const;

const iconColors = [
  "#DBEAFE",
  "#D1FAE5",
  "#F9F0F4",
  "#FCE7F3",
  "#FEF3C7",
  "#FFEDD5",
  "#F9F0F4",
  "#CCFBF1",
  "#FEE2E2",
];

const DeptIconSvg = ({
  id,
  color = "currentColor",
}: {
  id: string;
  color?: string;
}) => {
  const c = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
  } as const;
  switch (id) {
    case "building":
      return (
        <svg {...c}>
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
        </svg>
      );
    case "users":
      return (
        <svg {...c}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      );
    case "monitor":
      return (
        <svg {...c}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
    case "code":
      return (
        <svg {...c}>
          <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
        </svg>
      );
    case "chart":
      return (
        <svg {...c}>
          <path d="M4 19V5M4 19h16M8 17V11M12 17V7M16 17v-4" />
        </svg>
      );
    case "briefcase":
      return (
        <svg {...c}>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
        </svg>
      );
    case "settings":
      return (
        <svg {...c}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9c.3.6.9 1 1.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
        </svg>
      );
    default:
      return (
        <svg {...c}>
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
  }
};

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const CreateTeam = (props: CustomProps) => {
  const [department, setDepartment] = useState({
    department_name: "",
    department_code: "",
    description: "",
    icon: "building",
    _id: "",
  });
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [inputError, setInputError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState("");

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const response = await getAllDepartment();
      customErrorHandler(response);
      setDepartmentList(response.data || []);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const handleAddDepartment = async () => {
    if (!department.department_name.trim()) {
      setInputError(true);
      return;
    }
    try {
      setSaving(true);
      if (department._id) {
        const response = await editDepartment({
          _id: department._id,
          department_name: department.department_name,
        });
        openSuccessNotification(response.message);
      } else {
        const response = await addDepartment({
          department_name: department.department_name,
        });
        customErrorHandler(response);
        openSuccessNotification(response.message);
        props.setSelectedDept({
          department_name: response.data.department_name,
          _id: response.data._id,
          is_child: response.data.is_child,
        });
      }
      setDepartment({
        department_name: "",
        department_code: "",
        description: "",
        icon: "building",
        _id: "",
      });
      setInputError(false);
      init();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDepartment = async (dept_id: string) => {
    try {
      const response = await deleteDepartment(dept_id);
      customErrorHandler(response);
      setDepartmentList((list) => list.filter((d) => d._id !== dept_id));
      if (props.selectedDept?._id === dept_id) {
        props.setSelectedDept({});
      }
      openSuccessNotification(response.message);
      setConfirmDeleteId("");
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const filteredList = departmentList.filter((d) =>
    String(d.department_name || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const descLen = department.description.length;

  return (
    <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
      {/* Left form */}
      <div className="flex min-h-0 flex-col rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.03)]">
        <div className="mb-5">
          <h2 className="text-lg font-bold text-[#111827]">Create Department</h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Please create your organization department
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-3">
            <div className="min-w-0 flex-1">
              <YhInput
                label="Department Name"
                requiredMark
                placeholder="e.g. Information Technology"
                value={department.department_name}
                error={inputError ? "Department name is required" : ""}
                onChange={(e) => {
                  setInputError(false);
                  setDepartment((p) => ({
                    ...p,
                    department_name: e.target.value,
                  }));
                }}
              />
            </div>
            <div className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] text-[#A02448]">
              <DeptIconSvg id={department.icon} color="#A02448" />
            </div>
          </div>

          <div>
            <YhInput
              label="Department Code"
              placeholder="e.g. IT"
              value={department.department_code}
              onChange={(e) =>
                setDepartment((p) => ({
                  ...p,
                  department_code: e.target.value,
                }))
              }
            />
            <p className="mt-1 text-xs text-[#9CA3AF]">
              Unique code to identify the department
            </p>
          </div>

          <div>
            <label className="yh-label">Description</label>
            <div className="relative">
              <textarea
                className="yh-input min-h-[110px] resize-none py-3"
                placeholder="Enter department description..."
                maxLength={250}
                value={department.description}
                onChange={(e) =>
                  setDepartment((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
              />
              <span className="pointer-events-none absolute bottom-2 right-3 text-xs text-[#9CA3AF]">
                {descLen}/250
              </span>
            </div>
          </div>

          <div>
            <p className="yh-label">Department Icon</p>
            <p className="mb-2 text-xs text-[#9CA3AF]">
              Choose an icon to represent this department
            </p>
            <div className="flex flex-wrap gap-2">
              {DEPT_ICONS.map((icon) => {
                const active = department.icon === icon.id;
                return (
                  <button
                    key={icon.id}
                    type="button"
                    title={icon.label}
                    onClick={() =>
                      setDepartment((p) => ({ ...p, icon: icon.id }))
                    }
                    className={`flex h-11 w-11 items-center justify-center rounded-xl border transition ${
                      active
                        ? "border-[#A02448] bg-[#EEF0FF] text-[#A02448]"
                        : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <DeptIconSvg id={icon.id} />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-2">
            <YhButton
              variant="primary"
              size="sm"
              loading={saving}
              onClick={handleAddDepartment}
            >
              {department._id ? "Update Department" : "Add Department"}
            </YhButton>
          </div>
        </div>
      </div>

      {/* Right list */}
      <div className="flex min-h-0 flex-col rounded-2xl border border-[#EEF0F5] bg-white p-5 shadow-[0_4px_20px_rgba(15,23,42,0.03)]">
        <div className="mb-4 shrink-0">
          <h2 className="text-lg font-bold text-[#111827]">
            Existing Departments
          </h2>
          <p className="mt-1 text-sm text-[#6B7280]">
            Select a department to edit
          </p>
        </div>

        <div className="mb-3 shrink-0">
          <YhInput
            placeholder="Search department..."
            value={search}
            type="search"
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={<SearchSvg />}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-auto [scrollbar-width:thin]">
          {filteredList.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#9CA3AF]">
              No departments found
            </p>
          ) : (
            <ul className="divide-y divide-[#F3F4F6]">
              {filteredList.map((dept: any, index: number) => {
                const selected = props.selectedDept?._id === dept._id;
                const bg = iconColors[index % iconColors.length];
                return (
                  <li
                    key={dept._id}
                    className={`flex items-center gap-3 py-3 ${
                      selected ? "bg-[#F8F9FF]" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      onClick={() =>
                        props.setSelectedDept({
                          department_name: dept.department_name,
                          _id: dept._id,
                          is_child: dept.is_child,
                        })
                      }
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          selected
                            ? "border-[#A02448]"
                            : "border-[#D1D5DB]"
                        }`}
                      >
                        {selected ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-[#A02448]" />
                        ) : null}
                      </span>
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: bg }}
                      >
                        <DeptIconSvg id="building" color="#4338CA" />
                      </span>
                      <span className="truncate text-sm font-medium text-[#111827]">
                        {dept.department_name}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#A02448] transition hover:bg-[#EEF0FF]"
                      onClick={() => {
                        props.setSelectedDept({
                          department_name: dept.department_name,
                          _id: dept._id,
                          is_child: dept.is_child,
                        });
                        setDepartment({
                          department_name: dept.department_name,
                          department_code: "",
                          description: "",
                          icon: "building",
                          _id: dept._id,
                        });
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                      Edit
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {confirmDeleteId ? (
          <div className="mt-3 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-3">
            <p className="mb-2 text-sm text-[#991B1B]">
              Delete this department?
            </p>
            <div className="flex gap-2">
              <YhButton
                variant="danger"
                size="sm"
                onClick={() => handleRemoveDepartment(confirmDeleteId)}
              >
                Delete
              </YhButton>
              <YhButton
                variant="secondary"
                size="sm"
                onClick={() => setConfirmDeleteId("")}
              >
                Cancel
              </YhButton>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreateTeam;
